import { Alert, Button, Card, Input, Space, Tag as AntTag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Dialog as ZhDialog,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";

const USER_TAG_TYPES: Record<number, string> = {
    1: "了解渠道",
    2: "关注模块",
    3: "用户标签",
    4: "对接方式"
};

export interface UserTagName {
    id?: number;
    name: string;
}

export interface UserTagGroup {
    type: number;
    list: UserTagName[];
}

interface UserTagPageResult {
    list: UserTagGroup[];
    total: number;
}

interface UserTagQuery {
    page: number;
    size: number;
    type?: number;
    name?: string;
}

export interface UserTagsPageProps {
    loadTags?: (query: UserTagQuery) => Promise<UserTagPageResult>;
    createTag?: (params: { type: number; name: string[] }) => Promise<unknown>;
    updateTag?: (params: { type: number; dto: UserTagName[] }) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 用户标签分页接口。 */
export async function loadUserTags(query: UserTagQuery) {
    const response = await request.service.get<UserTagPageResult>("/shipping-plat-tag/paging", { params: query });
    return response.data;
}

/** 复用 Vue 用户标签新增接口，保留批量名称数组结构。 */
export async function createUserTag(params: { type: number; name: string[] }) {
    const response = await request.service.post("/shipping-plat-tag", params);
    return response.data;
}

/** 复用 Vue 用户标签修改接口，保留 type/dto 请求结构。 */
export async function updateUserTag(params: { type: number; dto: UserTagName[] }) {
    const response = await request.service.put("/shipping-plat-tag", params);
    return response.data;
}

/** 管理中心-用户标签，迁移 Vue 分组分页、新增和编辑交互。 */
export function UserTagsPage({
    loadTags = loadUserTags,
    createTag = createUserTag,
    updateTag = updateUserTag,
    permissions
}: UserTagsPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canAdd = currentPermissions.includes("ROLE_SHARE_STATISTICS_EXPORT");
    const canEdit = currentPermissions.includes("ROLE_PLATFORM_CLUE_W");
    const [rows, setRows] = useState<UserTagGroup[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [typeFilter, setTypeFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [dialog, setDialog] = useState<"add" | "edit">();
    const [formType, setFormType] = useState("");
    const [names, setNames] = useState<UserTagName[]>([{ name: "" }]);
    const [validationMessage, setValidationMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const refresh = async (requestedPage = page) => {
        try {
            const query: UserTagQuery = { page: requestedPage, size: 20 };
            if (typeFilter) query.type = Number(typeFilter);
            if (nameFilter) query.name = nameFilter;
            const result = await loadTags(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
            setErrorMessage("");
        } catch {
            setRows([]);
            setTotal(0);
            setErrorMessage("用户标签加载失败");
        }
    };

    useEffect(() => {
        setPage(1);
        void refresh(1);
        // 首次进入沿用 Vue ProTable 的默认分页请求。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadTags]);

    const openAdd = () => {
        setDialog("add");
        setFormType("");
        setNames([{ name: "" }]);
        setValidationMessage("");
    };

    const openEdit = useCallback((row: UserTagGroup) => {
        setDialog("edit");
        setFormType(String(row.type));
        setNames(row.list.map(item => ({ ...item })));
        setValidationMessage("");
    }, []);

    const submit = async () => {
        if (!formType) {
            setValidationMessage("请选择标签类型");
            return;
        }
        if (names.some(item => !item.name.trim())) {
            setValidationMessage("请输入标签名称");
            return;
        }
        setSaving(true);
        try {
            const type = Number(formType);
            if (dialog === "add") await createTag({ type, name: names.map(item => item.name.trim()) });
            if (dialog === "edit")
                await updateTag({ type, dto: names.map(item => ({ ...item, name: item.name.trim() })) });
            setDialog(undefined);
            await refresh();
        } catch {
            setErrorMessage("用户标签操作失败");
        } finally {
            setSaving(false);
        }
    };

    const updateName = (index: number, value: string) => {
        setNames(current => current.map((item, itemIndex) => (itemIndex === index ? { ...item, name: value } : item)));
        setValidationMessage("");
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue ZhTable 的分组展示和 operation slot。
    const userTagColumns = useMemo<TableColumnProps<UserTagGroup>[]>(
        () => [
            {
                key: "type",
                label: "类型",
                prop: "type",
                width: 400,
                render: ({ cellValue }) => <AntTag>{USER_TAG_TYPES[Number(cellValue)] || DEFAULT_VALUE}</AntTag>
            },
            {
                key: "names",
                label: "标签名称",
                prop: "list",
                render: ({ row }) => (row.list.length ? row.list.map(item => item.name).join("，") : DEFAULT_VALUE)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canEdit ? (
                        <Button type="link" onClick={() => openEdit(row)}>
                            修改
                        </Button>
                    ) : null
            }
        ],
        [canEdit, openEdit]
    );

    return (
        <section aria-label="user-tags-page">
            <Card>
                <ZhTable<UserTagGroup>
                    rowKey="type"
                    data={rows}
                    columns={userTagColumns}
                    emptyText="暂无用户标签"
                    pagination={false}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                用户标签
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space className="mb-4">
                                <select
                                    aria-label="标签类型筛选"
                                    value={typeFilter}
                                    onChange={event => setTypeFilter(event.target.value)}
                                >
                                    <option value="">请选择类型</option>
                                    {Object.entries(USER_TAG_TYPES).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                                <Input
                                    aria-label="标签名称筛选"
                                    placeholder="请输入标签名称"
                                    value={nameFilter}
                                    onChange={event => setNameFilter(event.target.value)}
                                />
                                <Button
                                    onClick={() => {
                                        setPage(1);
                                        void refresh(1);
                                    }}
                                >
                                    搜索
                                </Button>
                                {canAdd ? (
                                    <Button type="primary" onClick={openAdd}>
                                        新增
                                    </Button>
                                ) : null}
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={20}
                                total={total}
                                showSizeChanger={false}
                                // 保留 Vue 分页切换时的筛选条件，并按当前页重新请求。
                                onChange={nextPage => {
                                    setPage(nextPage);
                                    void refresh(nextPage);
                                }}
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                title={dialog === "add" ? "新增标签" : "修改标签"}
                open={dialog != null}
                width={400}
                appendToBody
                onCancel={() => setDialog(undefined)}
                footer={
                    <Space>
                        <Button onClick={() => setDialog(undefined)}>取消</Button>
                        <Button aria-label="保存" loading={saving} type="primary" onClick={() => void submit()}>
                            保存
                        </Button>
                    </Space>
                }
            >
                <Space direction="vertical" className="w-full">
                    {dialog === "add" ? (
                        <label>
                            标签类型
                            <select
                                aria-label="标签类型"
                                value={formType}
                                onChange={event => {
                                    setFormType(event.target.value);
                                    setValidationMessage("");
                                }}
                            >
                                <option value="">请选择标签类型</option>
                                {Object.entries(USER_TAG_TYPES).map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <Typography.Text>类型：{USER_TAG_TYPES[Number(formType)] || "--"}</Typography.Text>
                    )}
                    {names.map((item, index) => (
                        <Space key={item.id ?? index} className="w-full">
                            <Input
                                aria-label={`标签名称 ${index + 1}`}
                                maxLength={20}
                                placeholder="请输入标签名称"
                                value={item.name}
                                onChange={event => updateName(index, event.target.value)}
                            />
                            {names.length > 1 ? (
                                <Button
                                    aria-label={`删除标签 ${index + 1}`}
                                    type="link"
                                    onClick={() =>
                                        setNames(current => current.filter((_, itemIndex) => itemIndex !== index))
                                    }
                                >
                                    删除
                                </Button>
                            ) : null}
                        </Space>
                    ))}
                    {dialog === "add" && names.length < 10 ? (
                        <Button
                            aria-label="新增标签项"
                            type="link"
                            onClick={() => setNames(current => [...current, { name: "" }])}
                        >
                            + 新增
                        </Button>
                    ) : null}
                    {validationMessage ? (
                        <Typography.Text aria-live="polite" type="danger">
                            {validationMessage}
                        </Typography.Text>
                    ) : null}
                </Space>
            </ZhDialog>
        </section>
    );
}

export default UserTagsPage;
